import axios from 'axios'
import config from '@/config.js'
import exportConvert from '@/store/export.convert.js'
import { getStatic, getStaticDetail } from '@/services/data-source.js'
import {
  indexLemmaSensesByAssociation,
  normalizeLemma,
  normalizeStaticLemmaRelations,
  normalizeStaticLemmaPayload,
} from '@/services/lemma-data.js'
import {
  normalizeLemmaListPayload,
  queryStaticLemmaRows,
} from '@/services/lemma-list.js'
import {
  normalizeFuzzySearchPayload,
  queryStaticFuzzySearch,
} from '@/services/lemma-fuzzy-search.js'
import { searchAllWordForms } from '@/services/word-form-search.js'

const API_URL = config.apiUrl
const API_URL_CLIENTSIDE = config.apiUrlClientSide
let MAIN_URL = API_URL
let SUFFIX = ''
if (!config.editable) {
  MAIN_URL = API_URL_CLIENTSIDE
  SUFFIX = '.json'
}

const state = {
  lemmas: [],
  lemma: null,
  selectedLemmas: []
}

const mutations = {
  setLemmas(state, lemmas){
    state.lemmas = lemmas
  },
  async setLemma(state, lemma){
    state.lemma = lemma
  },
  setLemmaSenses(state, lemma){
    state.lemma.senseTree = lemma.senseTree
    state.lemma.senses = lemma.senses
    state.lemma.sensesDictIndex = lemma.sensesDictIndex
  },
  setLemmaSpellings(state, lemma){
    state.lemma.protospellingTree = lemma.protospellingTree
    state.lemma.protospellings = lemma.protospellings
    state.lemma.protospellingsDictIndex = lemma.protospellingsDictIndex
  }
}

const getters = {
}

const actions = {
  async loadLemma({ commit, dispatch }, id){
    commit('setLemma', null)
    try {
      const response = config.staticData
        ? await getStaticDetail('lemma', id)
        : await axios.get(`${API_URL}/lemma/${id}`)
      let lemma = response.data

      if (config.staticData) {
        lemma = normalizeStaticLemmaPayload(lemma)
        lemma = await exportConvert.convert('lemma.details', lemma)
        lemma = normalizeStaticLemmaRelations(lemma)
      }
      lemma = normalizeLemma(lemma)

      if (Array.isArray(lemma.protospellings)) {
        const protospellings = {}
        lemma.protospellings.forEach(protospelling => {
          protospellings[protospelling.id] = protospelling
        })
        lemma.protospellings = protospellings
      }

      if (Array.isArray(lemma.senses)) {
        lemma.senses = indexLemmaSensesByAssociation(lemma.senses)
      }

      commit('setLemma', lemma)
      return lemma
    } catch (error) {
      dispatch('loadingStatus', false)
      const notFound = error.response && error.response.status === 404
      const message = notFound
        ? `Lemma ${id} was not found`
        : error.message || `Lemma ${id} could not be loaded`
      dispatch('addNotification', {
        type: 'danger',
        text: message,
      })
      throw error
    }
  },
  loadSimpleLemma(_, id){
    let url = `${MAIN_URL}/lemma/${id}${SUFFIX}`
    if (!config.editable) {
      url = `${MAIN_URL}/lemma/${id.slice(0,2)}/${id}.json`
    }
    return new Promise((resolve) => {
      axios
        .get(url)
        .then(r => r.data)
        .then(lemma => {
          if (!config.editable) {
            exportConvert.convert("lemma", lemma).then((lemma) => {
              resolve(lemma)
            })
          }
          else {
            // commit('setLemma', lemma)
            resolve(lemma)
          }
        })
    })
  },
  loadLemmaSenses({ commit }, id){
    return new Promise((resolve) => {
      axios
        .get(`${MAIN_URL}/sense/${id}${SUFFIX}`)
        .then(r => r.data)
        .then(lemma => {
          commit('setLemmaSenses', lemma)
          resolve(lemma)
        })
    })
  },
  loadLemmaSpellings({ commit }, id){
    return new Promise((resolve) => {
      axios
        .get(`${MAIN_URL}/protospellings/${id}${SUFFIX}`)
        .then(r => r.data)
        .then(lemma => {
          commit('setLemmaSpellings', lemma)
          resolve(lemma)
        })
    })
  },
  async loadLemmaTokens(_, id){
    const response = config.staticData
      ? await getStatic(`lemma/${id.slice(0, 2)}/tokens-${id}.json`)
      : await axios.get(`${API_URL}/lemma/${id}/tokens`)
    let tokens = response.data
    if (config.staticData) {
      tokens = await exportConvert.convert('tokens', [tokens])
      tokens.tokensDictIndex = {}
    }
    return tokens
  },
  getLemmasWithEtymon(_, data) {
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/lemmas/with_etymon`, data)
        .then(response => {
          resolve(response)
        })
    })
  },
  searchLemmas({ commit }, data){
    let withoutArticle = data.withoutArticle ? true : false
    axios
      .get(`${API_URL}/lemmas/autocomplete?query=${data.query}&withoutArticle=${withoutArticle}&additionalData=${data.additionalData}&articleId=${data.articleId}`)
      .then(r => r.data)
      .then(lemmas => {
        commit('setLemmas', lemmas)
      })
  },
  async fuzzySearchLemmata({ dispatch }, data){
    try {
      if (config.staticData) {
        return await queryStaticFuzzySearch(data)
      }
      const response = await axios.post(`${API_URL}/lemmas/fuzzySearchLemmata`, data)
      const rows = normalizeFuzzySearchPayload(response.data)
      return { data: rows, count: rows.length, legacyFallback: false }
    } catch (error) {
      dispatch('addNotification', {
        type: 'danger',
        text: error.message || 'Lemma search could not be completed',
      })
      throw error
    }
  },
  async lemmaTable({ dispatch }, data){
    try {
      if (config.staticData) {
        const pattern = String(data.search || '').trim()
        if (!pattern) return await queryStaticLemmaRows(data)

        const wordFormResult = await searchAllWordForms({
          pattern,
          filters: ['internal', 'external', 'lemma'],
        }, queryStaticFuzzySearch)
        const response = await queryStaticLemmaRows(
          { ...data, search: '' },
          wordFormResult.lemmaIds
        )
        return {
          ...response,
          legacyFallback: wordFormResult.legacyFallback,
          compactWordFormSearch: wordFormResult.compact,
        }
      }
      const response = await axios.post(`${API_URL}/lemmas/lemma_table`, data)
      const rows = normalizeLemmaListPayload(response.data)
      return { data: rows, count: rows.length }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Lemmas could not be loaded'
      dispatch('addNotification', {
        type: 'danger',
        text: message
      })
      throw error
    }
  },
  updateLemma({ commit }, lemma){
    commit('setLemma', lemma)
  },
  addProtospellingToLemma(_, data){
    return new Promise((resolve) => {
      axios
        .post(`${MAIN_URL}/lemma/${data.lemmaId}/protospelling`, {
          protospellingId: data.protospellingId,
          lemmaProtospelling: data.lemmaProtospelling
        })
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  copySenseTreeTo(_, data){
    return new Promise((resolve) => {
      axios
        .post(`${MAIN_URL}/lemma/copy_sense_tree`, data)
        .then(response => {
          resolve(response)
        })
        .catch(async (err) => {
          const message = await err.response.data.message
          this.dispatch('addNotification', {
            type: 'danger',
            text: message || 'could not copy sense tree data. aborting.'
          })
        })
    })
  },
  addSenseToLemma(_, data){
    return new Promise((resolve) => {
      axios
        .post(`${MAIN_URL}/lemma/${data.lemmaId}/sense`, {
          senseId: data.senseId,
          lemmaSense: data.lemmaSense
        })
        .then(response => {
          resolve(response)
        })
        .catch(async (err) => {
          const message = await err.response.data.message
          this.dispatch('addNotification', {
            type: 'danger',
            text: message || 'could not add sense data to lemma'
          })
        })
    })
  },
  detachOccsRefs(_, data){
    return new Promise((resolve) => {
      axios
        .post(`${MAIN_URL}/lemma/detachOccsRefs`, data)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: 'could not detach Occurrences/References'
          })
        })
    })
  },
  saveLemmaOccurrenceTree(_, data){
    return new Promise((resolve) => {
      axios
        .post(`${MAIN_URL}/lemma/${data.lemmaId}/${data.objectType}/tree`, data.tree)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  mergeLemmas(_, data){
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/lemma/${data.mainLemmaId}/merge`, data)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  createLemma(_, lemma){
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/lemma`, lemma)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Lemma created`
          })
          // commit()
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
          // reject(error.response.data)
        })
    })
  },
  saveLemma(_, lemma){
    return new Promise((resolve) => {
      delete lemma.tokens
      delete lemma.references
      delete lemma.protospellings
      delete lemma.senses
      axios
        .post(`${API_URL}/lemma/${lemma.id}`, lemma)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Lemma saved`
          })
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
          // reject(error.response.data)
        })
    })
  },
  saveLemmaTree(_, lemma){
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/lemma/${lemma.id}/tree`, lemma)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Lemma saved`
          })
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
          // reject(error.response.data)
        })
    })
  },
  deleteLemma(_, lemmaId){
    return new Promise((resolve) => {
      axios
        .delete(`${API_URL}/lemma/${lemmaId}`)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Lemma deleted`
          })
          // commit()
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  saveEtymology(_, data){
    let url
    if (data.etymology.id.toString().substr(0, 4) == '_new'){
      // New
      url = `${API_URL}/lemma/${data.lemmaId}/etymology`
    }
    else {
      // Update
      url = `${API_URL}/lemma/${data.lemmaId}/etymology/${data.etymology.id}`
    }
    return new Promise((resolve) => {
      axios
        .post(url, data.etymology)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Etymology saved`
          })
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  deleteEtymology(_, data){
    return new Promise((resolve) => {
      axios
        .delete(`${API_URL}/lemma/${data.lemmaId}/etymology/${data.etymology.id}`)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Egymology deleted`
          })
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  addOccurrenceToSPS(_, data){
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/lemma/${data.lemmaId}/occurrence/add`, data)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  addReferenceToSPS(_, data){
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/lemma/${data.lemmaId}/reference/add`, data)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  deleteOccurrenceReference(_, data){
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/lemma/${data.lemmaId}/occurrence/delete`, data)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Occurrence/Reference removed`
          })
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  moveOccurrencesReferenceToLemma(_, data){
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/lemma/${data.lemmaId}/occurrences_references/move`, data)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  changeOccurrenceReference(_, data){
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/lemma/${data.lemmaId}/occurrence/change`, data)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Occurrence changed`
          })
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  saveProtospellingSense(_, data){
    let url = `${API_URL}/lemma/${data.lemmaId}/${data.objectType}/save`
    return new Promise((resolve) => {
      axios
        .post(url, data)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Data saved`
          })
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  saveLemmaOccurrence(_, data){
    let url = `${API_URL}/lemma/${data.lemmaId}/occurrence/${data.objectId}/save`
    return new Promise((resolve) => {
      axios
        .post(url, data)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Data saved`
          })
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  senseExists (_, senseDef) {
    let url = `${API_URL}/senses/exists?senseDef=${senseDef}`
    return new Promise((resolve) => {
      axios
        .get(url)
        .then(response => {
          resolve(response)
        })
    })
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
